import { apiFetch } from "./api";
import type {
  LogoUploadUrlRequest,
  LogoUploadUrlResponse,
  LogoVariant,
} from "@family/shared";

const ALLOWED_MIMES = new Set([
  "image/png",
  "image/svg+xml",
  "image/webp",
  "image/jpeg",
]);

/** 2 MB — matches the server-side cap. */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Upload a logo file for a family:
 *   1. Validate mime + size client-side (server validates again as truth).
 *   2. POST .../logo/:variant/upload-url → signed PUT URL + storagePath
 *   3. PUT bytes directly to GCS
 *   4. POST .../logo/:variant/finalize with the storagePath
 */
export async function uploadFamilyLogo(
  familySlug: string,
  variant: LogoVariant,
  file: File,
): Promise<void> {
  if (!ALLOWED_MIMES.has(file.type)) {
    throw new Error("Logo must be PNG, SVG, WebP, or JPEG.");
  }
  if (file.size > LOGO_MAX_BYTES) {
    throw new Error("Logo must be 2 MB or smaller.");
  }

  const reqBody: LogoUploadUrlRequest = {
    mimeType: file.type as LogoUploadUrlRequest["mimeType"],
    fileSize: file.size,
  };

  const signed = await apiFetch<LogoUploadUrlResponse>(
    `/families/${familySlug}/logo/${variant}/upload-url`,
    {
      method: "POST",
      body: JSON.stringify(reqBody),
    },
  );

  // PUT directly to GCS. Content-Type must match what we signed for.
  const putRes = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(`Upload failed: ${putRes.status} ${text}`);
  }

  await apiFetch(`/families/${familySlug}/logo/${variant}/finalize`, {
    method: "POST",
    body: JSON.stringify({ storagePath: signed.storagePath }),
  });
}

/**
 * Clear an uploaded logo (revert to bundled fallback).
 */
export async function clearFamilyLogo(
  familySlug: string,
  variant: LogoVariant,
): Promise<void> {
  await apiFetch(`/families/${familySlug}/logo/${variant}`, {
    method: "DELETE",
  });
}
